import { useMemo } from 'react';

import { useLocation } from 'react-router';

import { useTimeToExpire, useUserDetails } from '@/features/_shared/user';

export enum ExpiredBannerType {
  expired = 'expired',
  trialEndingSoon = 'trial-ending-soon',
  subscriptionEndingSoon = 'subscription-ending-soon'
}

type ExpiredBannerContent =
  | {
      type: ExpiredBannerType.expired;
    }
  | {
      type: ExpiredBannerType.trialEndingSoon;
      daysLeft: number;
    }
  | {
      type: ExpiredBannerType.subscriptionEndingSoon;
      subscription: string;
      daysLeft: number;
    }
  | null;

export const useExpiredBannerContent = (): ExpiredBannerContent => {
  const { isUserExpired, isTrial, currentSubscription } = useUserDetails();
  const timeToExpire = useTimeToExpire();
  const location = useLocation();

  return useMemo(() => {
    if (!currentSubscription) {
      return null;
    }
    if (location.pathname === '/manage-subscription') {
      return null;
    }

    if (isUserExpired) {
      return { type: ExpiredBannerType.expired };
    }

    if (timeToExpire && currentSubscription) {
      if (isTrial) {
        return {
          type: ExpiredBannerType.trialEndingSoon,
          daysLeft: timeToExpire.days + 1
        };
      }
      if (timeToExpire.days < 14) {
        return {
          type: ExpiredBannerType.subscriptionEndingSoon,
          subscription: currentSubscription,
          daysLeft: timeToExpire.days + 1
        };
      }
    }

    return null;
  }, [isUserExpired, timeToExpire, isTrial, currentSubscription, location.pathname]);
};
